<template>
  <div v-if="isShowPpc" :class="{ 'ppc-wrapper': !isProductPerksEnabled }">
    <component
      :is="PpcContent"
      v-bind="ppcProps"
      :reward-badge-enabled
      :is-perks-placement="isPerksPlacement"
      @open-ppc-modal="openPpcModal"
      @open-details-modal="openDetailModal"
    >
      <template #loyalty-icon="iconProps">
        <PpcIcon v-bind="iconProps" />
      </template>
    </component>

    <component
      :is="ActiveModal"
      v-if="!!ActiveModal"
      v-bind="{ badgesList, name: `${modalName}-modal`, 'modal-details': modalProps }"
      @close="modalName = ''"
    />
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue';
import { fireDetailsAnalytics } from '@nuxt-common/media-card-large/app/modules/price/components/utils.common';

import { useEnvProps } from '~/composables/useEnvProps';
import { usePpc } from '~/modules/ppc/composables/usePPC';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import once from '~/utils/once';
import { ppcDetailsClicked } from '../../../features/analytics/productPerks';
import { preFetchAsyncComponents } from '~/utils/preFetchAsyncComponents';

import PpcIcon from './PpcIcon.vue';

const props = defineProps({
  priceId: {
    type: String,
    default: '',
  },
  priceOfferDescription: {
    type: String,
    default: '',
  },
  badgesList: {
    type: Array,
    default: () => [],
  },
  isPerksPlacement: {
    type: Boolean,
    default: false,
  },
});

const PPC_MODAL = 'ppc';
const PRICE_DETAILS_MODAL = 'price-details';

const { brand } = useEnvProps();
const { ppcProps, modalProps, isShowPpc, rewardBadgeEnabled } = usePpc();
const { isProductPerksEnabled } = useFeatureEligibility();
const firePerksAnalytics = once(ppcDetailsClicked);

const modalName = ref('');

const PpcContent = defineAsyncComponent(() => import(/* webpackChunkName: "PpcContent" */ `./${brand}/PPC.${brand}.vue`));

const Modals = {
  [PRICE_DETAILS_MODAL]: defineAsyncComponent(
    () => import(/* webpackChunkName: 'DetailsModal bcom' */ '@nuxt-common/media-card-large/app/modules/price/components/DetailsModal.vue'),
  ),
  [PPC_MODAL]: defineAsyncComponent(() => import(/* webpackChunkName: 'PpcModal' */ './PpcModal.vue')),
};
const ActiveModal = computed(() => Modals[modalName.value]);

const openPpcModal = () => {
  if (isProductPerksEnabled) {
    firePerksAnalytics();
  }
  modalName.value = PPC_MODAL;
};

const openDetailModal = () => {
  fireDetailsAnalytics({
    id: props.priceId,
    value: props.priceOfferDescription,
  });
  modalName.value = PRICE_DETAILS_MODAL;
};

onMounted(async () => {
  // prefetch all modals on mount
  Object.keys(Modals).forEach((modalName) => {
    preFetchAsyncComponents(Modals[modalName]);
  });

  if (import.meta.env.VITE_ENTRY_POINT === 'bcom') {
    try {
      // Vite compiles the SCSS and injects it into the DOM lazily
      await import('../assets/modal-header.bcom.scss');
    } catch (e) {
      console.error('Failed to lazy load modal header styles', e);
    }
  }
});
</script>

<style lang="scss" scoped>
.loyalty-icon-wrapper {
  max-width: 24px;
  max-height: 24px;

  &.loyalty-icon-wrapper--redesign {
    margin-right: rem-calc(12px);
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@import '~settingSCSS';

.ppc-wrapper:not(.hide):has(+ .label) {
  @include margin-bottom-xs;
}
</style>

<style lang="scss" brand="mcom" scoped>
.loyalty-icon-wrapper {
  .star-rewards-icon {
    display: flex;
    flex-shrink: 0;
  }
}
</style>
